import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useProject } from "../context/ProjectContext";
import { api, fmtErr } from "../lib/api";
import { industryByKey } from "../lib/constants";
import { ArrowLeft, RocketLaunch } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import AutofillDrop from "../components/intake/AutofillDrop";
import SpecReview from "../components/intake/SpecReview";
import SpecTemplateDrop from "../components/intake/SpecTemplateDrop";
import SpecDelta from "../components/intake/SpecDelta";
import { Sparkle, Table } from "@phosphor-icons/react";

const MODES = [
  { key: "tender", icon: Sparkle, title: "From a tender / enquiry",
    blurb: "The client's enquiry, requisition or tender document. Cognara AI pulls the commercial header, every equipment item and every technical parameter, each with the quote it came from." },
  { key: "template", icon: Table, title: "From a specification template",
    blurb: "A spec sheet or datasheet template in any format. Cognara AI maps every printed row onto the data model and shows the delta — mapped, converted, missing and unknown parameters." },
];

export default function NewProject() {
  const { user } = useAuth();
  const { setPid, reload: reloadProjects } = useProject();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [eqMeta, setEqMeta] = useState(null);
  const [eqTypes, setEqTypes] = useState([]);
  const [form, setForm] = useState({
    client_id: "", industry: "", product_summary: "", reference_po: "",
    delivery_date: "", priority: "medium", engineer_id: "", workspace_id: "",
    equipment_type: "", standards_basis: "iec", equipment_facets: {},
  });
  const [busy, setBusy] = useState(false);
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", email: "", phone: "", address: "" });
  const [af, setAf] = useState(null);
  const [mode, setMode] = useState("tender");
  const [tpl, setTpl] = useState(null);
  const [tplAccepted, setTplAccepted] = useState({});
  const [tplCustom, setTplCustom] = useState({});
  const [saveTpl, setSaveTpl] = useState(true);
  const [tplName, setTplName] = useState("");
  const [eqItems, setEqItems] = useState([]);
  const [afFields, setAfFields] = useState({});
  const [accepted, setAccepted] = useState({});

  const orgIndustries = user?.org?.industries || [];
  const selType = eqTypes.find((t) => t.key === form.equipment_type) || null;
  const industryOptions = selType?.pack_key && !orgIndustries.includes(selType.pack_key)
    ? [selType.pack_key, ...orgIndustries] : orgIndustries;
  const facetGroups = eqMeta?.facet_groups?.[selType?.family] || [];

  useEffect(() => {
    Promise.all([api.get("/clients"), api.get("/users"), api.get("/workspaces"),
      api.get("/equipment/meta"), api.get("/equipment/types")])
      .then(([c, u, w, m, t]) => {
        setClients(c.data); setUsers(u.data); setWorkspaces(w.data);
        setEqMeta(m.data); setEqTypes(t.data);
      })
      .catch((e) => toast.error(fmtErr(e)));
  }, []);

  useEffect(() => {
    const et = params.get("equipment_type");
    const ind = params.get("industry");
    const basis = params.get("basis");
    if (et || ind || basis) {
      setForm((f) => ({
        ...f,
        equipment_type: et || f.equipment_type,
        industry: ind || f.industry,
        standards_basis: basis || f.standards_basis,
      }));
    }
  }, [params, user?.org?.industries]);

  const pickType = (key) => {
    const t = eqTypes.find((x) => x.key === key);
    setForm((f) => ({
      ...f,
      equipment_type: key,
      industry: t ? t.pack_key : f.industry,
      equipment_facets: {},
      product_summary: f.product_summary || (t ? t.name : ""),
    }));
  };

  const applyTemplate = (r) => {
    setTpl(r);
    setTplName(r.template?.name || "Specification template");
    setTplAccepted(Object.fromEntries((r.rows || [])
      .filter((x) => x.field_id && ["mapped", "converted"].includes(x.status))
      .map((x) => [x.field_id, true])));
    setTplCustom({});
    setForm((f) => ({
      ...f,
      industry: r.pack?.key || f.industry,
      product_summary: f.product_summary || r.template?.equipment_summary || "",
    }));
  };

  const editTemplateRow = (rowNo, val) =>
    setTpl((t) => ({ ...t, rows: t.rows.map((r) => (r.row === rowNo ? { ...r, value: val } : r)) }));

  const createProject = async () => {
    if (!form.client_id || !form.industry || !form.product_summary.trim()) {
      toast.error("Client, industry and product description are required");
      return;
    }
    setBusy(true);
    try {
      const specs = {};
      if (mode === "template" && tpl) {
        (tpl.rows || []).forEach((r) => {
          if (r.field_id && tplAccepted[r.field_id] && r.value !== null && r.value !== undefined && r.value !== "")
            specs[r.field_id] = r.value;
        });
      } else {
        Object.entries(afFields).forEach(([fid, v]) => {
          if (accepted[fid] && v.value !== null && v.value !== undefined && v.value !== "") specs[fid] = v.value;
        });
      }
      const { data } = await api.post("/projects", {
        ...form, engineer_id: form.engineer_id || null, workspace_id: form.workspace_id || null,
        equipment_type: form.equipment_type || null, specs,
      });
      if (mode === "template" && tpl?.staging_token) {
        try {
          const { data: c } = await api.post(`/projects/${data.id}/spec-template/commit`, {
            staging_token: tpl.staging_token, rows: tpl.rows,
            accepted: Object.keys(tplAccepted).filter((k) => tplAccepted[k]),
            add_custom: Object.keys(tplCustom).filter((k) => tplCustom[k]).map(Number),
            save_template: saveTpl, template_name: tplName, client_id: form.client_id,
          });
          toast.success(`Specification template applied — ${c.applied} parameter(s)`
            + (c.custom_added ? `, ${c.custom_added} new data-model field(s)` : "")
            + (c.saved_template_id ? ", mapping saved for reuse" : ""));
        } catch (e) { toast.warning(`Project created, but the template commit failed: ${fmtErr(e)}`); }
      } else if (af?.staging_token) {
        const prov = {};
        Object.keys(specs).forEach((fid) => { prov[fid] = afFields[fid]; });
        const extra = eqItems.filter((i) => i.include !== false);
        try {
          const { data: c } = await api.post(`/projects/${data.id}/autofill/commit`, {
            staging_token: af.staging_token, fields: prov, equipment_items: extra });
          if (c.equipment_items_created)
            toast.success(`${c.equipment_items_created} additional equipment item(s) created from the tender`);
        } catch { toast.warning("Project created, but attaching the source documents failed"); }
      }
      const n = Object.keys(specs).length;
      toast.success(mode === "template"
        ? `Project ${data.project_code} created${n ? ` with ${n} parameter${n > 1 ? "s" : ""} from the specification template` : ""}`
        : `Project ${data.project_code} created${n ? ` with ${n} autofilled parameter${n > 1 ? "s" : ""}` : " — upload client documents in the Intake tab"}`);
      setPid(data.id);
      await reloadProjects();
      nav(`/projects/${data.id}?tab=intake`);
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const applyAutofill = async (r) => {
    setAf(r);
    setEqItems((r.equipment_items || []).map((i) => ({ ...i, include: true })));
    setAfFields(r.fields || {});
    setAccepted(Object.fromEntries(Object.entries(r.fields || {}).map(([k, v]) => [k, v.status === "found"])));
    const h = r.header || {};
    let clientId = h.client_id;
    if (!clientId && h.client_name) {
      try {
        const { data } = await api.post("/clients", { name: h.client_name });
        clientId = data.id;
        setClients((cs) => [...cs, { id: data.id, name: h.client_name }]);
        toast.info(`Client "${h.client_name}" added from the document`);
      } catch { /* keep the field blank for the user to pick */ }
    }
    setForm((f) => ({
      ...f,
      client_id: clientId || f.client_id,
      industry: h.industry || f.industry,
      product_summary: h.product_summary || f.product_summary,
      equipment_type: h.equipment_type || f.equipment_type,
      standards_basis: h.standards_basis || f.standards_basis,
      reference_po: h.reference_po || f.reference_po,
      delivery_date: h.delivery_date || f.delivery_date,
      priority: h.priority || f.priority,
    }));
  };

  const addClient = async () => {
    if (!newClient.name) return;
    try {
      const { data } = await api.post("/clients", newClient);
      toast.success("Client added");
      setClients((cs) => [...cs, { ...newClient, id: data.id }]);
      setForm({ ...form, client_id: data.id });
      setShowNewClient(false);
      setNewClient({ name: "", email: "", phone: "", address: "" });
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const selCls = "bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]";

  return (
    <div className="p-6 max-w-[1500px]" data-testid="new-project-page">
      <button onClick={() => nav("/projects")} data-testid="np-back"
        className="flex items-center gap-1.5 text-xs text-[var(--c-muted)] hover:text-[var(--c-text)] transition-colors duration-150 mb-4">
        <ArrowLeft size={13} /> Projects
      </button>
      <h2 className="text-2xl tracking-tight font-medium text-[var(--c-text)]">New Project</h2>
      <p className="text-xs text-[var(--c-muted)] mt-1.5 mb-6 max-w-xl">
        Create the project first — document upload, AI parsing, gap detection and similar-project baselines are available afterwards in the project's <span className="text-[var(--c-text)]">Intake</span> tab.
      </p>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] items-start">
      <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2" data-testid="np-mode-cards">
        {MODES.map((m) => (
          <button key={m.key} data-testid={`np-mode-${m.key}`} onClick={() => setMode(m.key)}
            className={`text-left rounded-xl border p-4 transition-colors duration-150 ${
              mode === m.key
                ? "border-[var(--c-accent)] bg-[var(--c-accent)]/[0.06]"
                : "border-[var(--c-border)] bg-[var(--c-surface)] hover:border-[var(--c-accent)]/50"}`}>
            <div className="flex items-center gap-2">
              <m.icon size={15} className={mode === m.key ? "text-[var(--c-accent)]" : "text-[var(--c-muted)]"} />
              <span className="text-xs font-medium text-[var(--c-text)]">{m.title}</span>
            </div>
            <div className="text-[10px] text-[var(--c-muted)] mt-1.5 leading-relaxed">{m.blurb}</div>
          </button>
        ))}
      </div>

      {mode === "template" ? (
        <>
          <SpecTemplateDrop onResult={applyTemplate} disabled={busy} />
          {tpl && (
            <>
              <SpecDelta result={tpl} accepted={tplAccepted} custom={tplCustom}
                onToggle={(fid, on) => setTplAccepted((a) => ({ ...a, [fid]: on }))}
                onToggleCustom={(rowNo, on) => setTplCustom((c) => ({ ...c, [rowNo]: on }))}
                onEdit={editTemplateRow} />
              <div className="flex flex-wrap items-center gap-3 rounded-sm border border-[var(--c-border)] bg-[var(--c-bg)] px-4 py-2.5"
                data-testid="np-save-template">
                <label className="flex items-center gap-2 text-[11px] text-[var(--c-text)] cursor-pointer">
                  <input type="checkbox" className="accent-[var(--c-accent)]" checked={saveTpl}
                    data-testid="np-save-template-toggle" onChange={(e) => setSaveTpl(e.target.checked)} />
                  Save this mapping for reuse
                </label>
                <Input data-testid="np-template-name" value={tplName} onChange={(e) => setTplName(e.target.value)}
                  placeholder="Template name" className={`${selCls} h-8 flex-1 min-w-[12rem] text-xs`} />
              </div>
            </>
          )}
        </>
      ) : (
      <div>
        <AutofillDrop onResult={applyAutofill} disabled={busy} />
      </div>
      )}

      {mode === "tender" && af && (
        <div className="space-y-3" data-testid="np-autofill-result">
          <div className="flex flex-wrap items-center gap-4 rounded-sm border border-[var(--c-border)] bg-[var(--c-bg)] px-4 py-2.5 font-mono text-[11px]">
            <span className="text-[var(--c-green)]">Extracted: {af.summary.extracted}</span>
            <span className="text-[var(--c-accent)]">AI-inferred: {af.summary.inferred}</span>
            <span className="text-[var(--c-amber)]">Open: {af.summary.missing}</span>
            <span className="text-[var(--c-muted)]">{af.pack?.name}</span>
            {(af.skipped_files || []).length > 0 && (
              <span className="text-[var(--c-muted)]" title={af.skipped_files.join(", ")}>Unreadable: {af.skipped_files.length}</span>
            )}
          </div>
          {af.header?.notes && <div className="text-[11px] text-[var(--c-muted)] leading-relaxed">{af.header.notes}</div>}
          {eqItems.length > 1 && (
            <div className="border border-[var(--c-border)] rounded-sm overflow-hidden" data-testid="np-equipment-items">
              <div className="px-3 py-2 bg-[var(--c-surface-2)] text-[11px] font-medium text-[var(--c-text)]">
                {eqItems.length} equipment items found in this tender — each gets its own specs, design, drawings and 3D model
              </div>
              {eqItems.map((i, idx) => (
                <label key={idx} data-testid={`np-eq-item-${idx}`}
                  className="flex items-start gap-3 px-3 py-2 border-t border-[var(--c-border)] cursor-pointer">
                  <input type="checkbox" className="mt-0.5 accent-[var(--c-accent)]"
                    checked={i.include !== false} disabled={i.primary}
                    onChange={(e) => setEqItems((xs) => xs.map((x, k) => k === idx ? { ...x, include: e.target.checked } : x))} />
                  <div className="min-w-0">
                    <div className="text-[11px] text-[var(--c-text)] truncate">
                      {i.product_summary} {i.quantity > 1 && <span className="font-mono text-[var(--c-accent)]">x{i.quantity}</span>}
                      {i.primary && <span className="ml-2 text-[9px] uppercase text-[var(--c-muted)]">primary</span>}
                    </div>
                    <div className="text-[10px] text-[var(--c-muted)] font-mono">
                      {i.pack_name}{i.item_code ? ` · ${i.item_code}` : i.tag ? ` · ${i.tag}` : ""}
                      {i.summary ? ` · ${i.summary.extracted} extracted, ${i.summary.inferred} inferred` : ""}
                    </div>
                    {i.source_quote && <div className="text-[10px] text-[var(--c-muted)] italic mt-0.5 truncate">"{i.source_quote}"</div>}
                  </div>
                </label>
              ))}
            </div>
          )}

          <SpecReview fields={afFields} gaps={af.gaps} accepted={accepted}
            onToggle={(fid, on) => setAccepted((a) => ({ ...a, [fid]: on }))}
            onEdit={(fid, val) => setAfFields((fs) => ({ ...fs, [fid]: { ...fs[fid], value: val } }))} />
          <div className="text-[10px] text-[var(--c-muted)] leading-relaxed">
            Values switched on are written into the project specification on create. AI-inferred defaults are off by
            default — review each one before accepting. Commercial fields (PO reference, delivery date, client) are
            only ever taken from the document, never inferred.
          </div>
        </div>
      )}

      {mode === "tender" && !af && (
        <div className="rounded-lg border border-dashed border-[var(--c-border)] bg-[var(--c-bg)] p-5 space-y-3"
          data-testid="np-autofill-hint">
          <div className="text-[11px] font-medium text-[var(--c-text)]">What Autofill does for you</div>
          {[["Reads the enquiry", "PDF, Word, Excel, scanned spec sheets or a photo — every technical parameter is pulled out with the quote it came from."],
            ["Finds every equipment type", "A tender with transformers, switchgear and a capacitor bank becomes separate equipment items you can tick on or off — each one gets its own design, drawings, BOM and 3D model."],
            ["Flags the gaps", "Anything the document does not state is listed as open so you can chase it before quoting."]].map(([h, b]) => (
            <div key={h} className="flex gap-2.5">
              <div className="mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--c-accent)]" />
              <div>
                <div className="text-[11px] text-[var(--c-text)]">{h}</div>
                <div className="text-[10px] text-[var(--c-muted)] leading-relaxed">{b}</div>
              </div>
            </div>
          ))}
          <div className="text-[10px] text-[var(--c-muted)] leading-relaxed border-t border-[var(--c-border)] pt-2.5">
            No document to hand? Fill the form on the right — you can autofill later from the project's Intake tab.
          </div>
        </div>
      )}
      </div>

      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5 space-y-4" data-testid="np-step-info">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-[var(--c-muted)] text-xs">Client *</Label>
            <div className="flex gap-2">
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="np-client" className={`${selCls} flex-1`}><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                  {clients.map((c) => <SelectItem key={c.id} value={c.id} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" data-testid="np-add-client" onClick={() => setShowNewClient(true)}
                className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm">Add New</Button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[var(--c-muted)] text-xs">Industry *</Label>
            <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
              <SelectTrigger data-testid="np-industry" className={selCls}><SelectValue placeholder="Select industry" /></SelectTrigger>
              <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                {industryOptions.map((k) => <SelectItem key={k} value={k} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{industryByKey(k).name || k.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[var(--c-muted)] text-xs">Product name / description *</Label>
          <Input data-testid="np-summary" value={form.product_summary} onChange={(e) => setForm({ ...form, product_summary: e.target.value })}
            className={selCls} placeholder="e.g., 5 MVA 33/11kV Power Transformer" />
        </div>
        <div className="border-t border-[var(--c-border)] pt-4 space-y-3" data-testid="np-equipment-block">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-[var(--c-muted)] text-xs">Equipment type</Label>
              <select data-testid="np-equipment-type" value={form.equipment_type}
                onChange={(e) => pickType(e.target.value)}
                className="w-full h-9 text-sm bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm px-2 text-[var(--c-text)]">
                <option value="">— Not specified —</option>
                {(eqMeta?.families || []).map((fam) => (
                  <optgroup key={fam.key} label={fam.name}>
                    {eqTypes.filter((t) => t.family === fam.key).map((t) => (
                      <option key={t.key} value={t.key}>{`${t.category} · ${t.name}`}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <div className="text-[10px] text-[var(--c-muted)]">
                Browse the full taxonomy in <span className="text-[var(--c-text)]">Equipment Catalog</span>.
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[var(--c-muted)] text-xs">Standards basis</Label>
              <Select value={form.standards_basis} onValueChange={(v) => setForm({ ...form, standards_basis: v })}>
                <SelectTrigger data-testid="np-standards-basis" className={selCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                  {(eqMeta?.standards_bases || []).map((b) => (
                    <SelectItem key={b.key} value={b.key} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {selType && (
            <div className="bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm p-3 space-y-3" data-testid="np-type-detail">
              <div className="text-[11px] text-[var(--c-muted)] leading-relaxed">{selType.description}</div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-[10px] font-mono text-[var(--c-muted)]">
                <span>Rating: <span className="text-[var(--c-text)]">{selType.rating_range || "—"}</span></span>
                <span>Voltage: <span className="text-[var(--c-text)]">{selType.voltage_range || "—"}</span></span>
                <span>Standards: <span className="text-[var(--c-text)]">{(selType.standards?.[form.standards_basis] || []).join(", ") || "—"}</span></span>
              </div>
              {Object.keys(selType.facets || {}).length > 0 && (
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(selType.facets).map(([g, opts]) => (
                    <div key={g} className="space-y-1">
                      <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--c-muted)]">
                        {(facetGroups.find((x) => x.key === g)?.label) || g}
                      </div>
                      <select data-testid={`np-facet-${g}`} value={form.equipment_facets[g] || ""}
                        onChange={(e) => setForm({ ...form, equipment_facets: { ...form.equipment_facets, [g]: e.target.value } })}
                        className="w-full text-xs bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm px-2 py-1.5 text-[var(--c-text)]">
                        <option value="">Not specified</option>
                        {(facetGroups.find((x) => x.key === g)?.options || opts).map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-[var(--c-muted)] text-xs">Reference / PO number</Label>
            <Input data-testid="np-ref" value={form.reference_po} onChange={(e) => setForm({ ...form, reference_po: e.target.value })}
              className={`${selCls} font-mono`} placeholder="PO-2026-0184" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[var(--c-muted)] text-xs">Delivery date</Label>
            <Input data-testid="np-delivery" type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })}
              className={`${selCls} font-mono`} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-[var(--c-muted)] text-xs">Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger data-testid="np-priority" className={selCls}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                <SelectItem value="high" className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">High</SelectItem>
                <SelectItem value="medium" className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">Medium</SelectItem>
                <SelectItem value="low" className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[var(--c-muted)] text-xs">Assign engineer</Label>
            <Select value={form.engineer_id} onValueChange={(v) => setForm({ ...form, engineer_id: v })}>
              <SelectTrigger data-testid="np-engineer" className={selCls}><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                {users.filter((u) => ["engineer", "lead", "admin"].includes(u.role)).map((u) => (
                  <SelectItem key={u.id} value={u.id} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {workspaces.length > 0 && (
          <div className="space-y-1.5">
            <Label className="text-[var(--c-muted)] text-xs">Workspace</Label>
            <Select value={form.workspace_id || "none"} onValueChange={(v) => setForm({ ...form, workspace_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="np-workspace" className={selCls}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                <SelectItem value="none" className="text-[var(--c-muted)] focus:bg-[var(--c-surface-2)]">— No workspace (visible org-wide) —</SelectItem>
                {workspaces.map((w) => <SelectItem key={w.id} value={w.id} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{w.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-[10px] text-[var(--c-muted)]">Workspace projects are visible only to allocated workspace members.</div>
          </div>
        )}
        <div className="flex justify-end pt-2">
          <Button data-testid="np-create" onClick={createProject}
            disabled={busy || !form.client_id || !form.industry || !form.product_summary.trim()}
            className="bg-[var(--c-green)] hover:bg-[var(--c-green)]/85 text-white rounded-sm">
            <RocketLaunch size={14} className="mr-1.5" /> {busy ? "Creating…" : "Create Project"}
          </Button>
        </div>
      </div>
      </div>

      <Dialog open={showNewClient} onOpenChange={setShowNewClient}>
        <DialogContent className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)]" data-testid="np-new-client-dialog">
          <DialogHeader>
            <DialogTitle className="text-[var(--c-text)]">Add New Client</DialogTitle>
            <DialogDescription className="sr-only">Quick client creation</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input data-testid="npc-name" placeholder="Client name *" value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} className={selCls} />
            <Input data-testid="npc-email" placeholder="Email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} className={selCls} />
            <Button data-testid="npc-save" onClick={addClient} className="w-full bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm">Add Client</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
